/**
 * SPEC §9.3 — geohash prefixes are a free LOD pyramid. Zoom picks a precision
 * (with hysteresis so crossing a threshold does not thrash), the viewport
 * picks the prefixes, and §3.1's prefix rule bounds every request.
 */
import { cellsInBBox, type Bounds } from '@latency-globe/shared';

/** Switch *up* to precision p when zoom ≥ UP[p]; switch down 0.3 below it. */
const UP: Record<number, number> = { 3: 3, 4: 5.5, 5: 8 };
const HYSTERESIS = 0.3;
export const MAX_PREFIXES = 12;

export function precisionForZoom(zoom: number, current: number, maxPrecision = 5): number {
  let p = 2;
  for (const q of [3, 4, 5]) {
    const threshold = UP[q]! - (current >= q ? HYSTERESIS : 0);
    if (zoom >= threshold) p = q;
  }
  return Math.min(p, maxPrecision);
}

/**
 * The prefixes (length precision − 2) a viewport needs, or null when it needs
 * more than MAX_PREFIXES — the caller then drops a precision level.
 */
export function prefixesFor(bounds: Bounds, precision: number): string[] | null {
  if (precision <= 2) return [''];
  return cellsInBBox(bounds, precision - 2, MAX_PREFIXES);
}

/** Pick the finest precision whose prefix count fits the budget. */
export function planView(bounds: Bounds, zoom: number, current: number, maxPrecision = 5): { precision: number; prefixes: string[] } {
  let precision = precisionForZoom(zoom, current, maxPrecision);
  for (; precision > 2; precision--) {
    const prefixes = prefixesFor(bounds, precision);
    if (prefixes) return { precision, prefixes };
  }
  return { precision: 2, prefixes: [''] };
}

/** Small LRU keyed by string. */
export class LRU<V> {
  private map = new Map<string, V>();
  constructor(private capacity: number) {}
  get(k: string): V | undefined {
    const v = this.map.get(k);
    if (v !== undefined) { this.map.delete(k); this.map.set(k, v); }
    return v;
  }
  set(k: string, v: V): void {
    this.map.delete(k);
    this.map.set(k, v);
    while (this.map.size > this.capacity) this.map.delete(this.map.keys().next().value!);
  }
  has(k: string): boolean { return this.map.has(k); }
  clear(): void { this.map.clear(); }
}
